import http from '@frontend/api/http';
import { HYPATIA_URL } from '@frontend/envConfig';

type AgentDirectoryTag = {
  id: string;
  name: string;
  description: string;
  created_at: string;
  usage_count: number;
};

type DirectoryAgent = {
  agent_id: string;
  agent_type: 'specialized' | 'workflow';
  name: string;
  description: string;
  directory_version: number;
  agent_version: string;
  tags: AgentDirectoryTag[];
  is_featured: boolean;
  publisher_email: string;
  published_at: string;
  details_url: string | null;
  is_installed: boolean;
  installed_version: number | null;
};

type DirectoryBrowseResponse = {
  agents: DirectoryAgent[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
};

type PublishAgentRequest = {
  agent_type: 'specialized' | 'workflow';
  opal_instance_id: string;
  publisher_email: string;
  tags?: string[];
  is_featured?: boolean;
  is_hidden?: boolean;
  details_url?: string;
  release_notes?: string;
  etag?: string;
};

type PublishAgentResponse = {
  success: boolean;
  message: string;
  data: Record<string, unknown>;
};

type InstallAgentRequest = {
  opal_instance_id: string;
  user_id: string;
  directory_version?: number;
  force_update?: boolean;
};

type InstallAgentResponse = {
  agent_id: string;
  directory_version: number;
  status: string;
  message: string;
  installed_at: string;
  dependencies_installed: string[];
};

type AgentDependency = {
  agent_id: string;
  directory_version: number;
};

type AgentDirectoryDetails = {
  id: string;
  agent_id: string;
  agent_type: 'specialized' | 'workflow';
  name: string;
  description: string;
  directory_version: number;
  agent_version: string;
  publisher_email: string;
  published_at: string;
  tags: AgentDirectoryTag[];
  is_featured: boolean;
  is_hidden: boolean;
  details_url: string | null;
  release_notes: string;
  enabled_tools?: string[] | null;
  export_schema_version: string;
  etag: string;
  dependencies?: AgentDependency[];
};

export function publishAgentToDirectory(agentId: string, request: PublishAgentRequest) {
  return async () => {
    const res = await http.put<PublishAgentResponse>(
      `${HYPATIA_URL}/api/v1/directory/agents/${agentId}`,
      request
    );
    return res.data;
  };
}

export function installAgentFromDirectory(agentId: string, request: InstallAgentRequest) {
  return async () => {
    const res = await http.post<InstallAgentResponse>(
      `${HYPATIA_URL}/api/v1/directory/install/${agentId}`,
      request
    );
    return res.data;
  };
}

export function getAgentsFromDirectory({
  tags,
  is_featured,
  search,
  page = 1,
  limit = 20,
}: {
  tags?: string[];
  is_featured?: boolean;
  search?: string;
  page?: number;
  limit?: number;
} = {}) {
  return async () => {
    const params: Record<string, unknown> = {
      page,
      limit,
    };

    if (tags && tags.length > 0) {
      params.tags = tags;
    }
    if (is_featured !== undefined) {
      params.is_featured = is_featured;
    }
    if (search) {
      params.search = search;
    }

    const res = await http.get<DirectoryBrowseResponse>(`${HYPATIA_URL}/api/v1/directory/agents`, {
      params,
    });
    return res.data;
  };
}

getAgentsFromDirectory.key = (
  tags?: string[],
  is_featured?: boolean,
  search?: string,
  page = 1,
  limit = 20
) => ['opal:directory-agents', tags, is_featured, search, page, limit] as const;

export function getAgentDirectoryDetails(agentId: string, directoryVersion?: number) {
  return async () => {
    const params: Record<string, unknown> = {};

    if (directoryVersion !== undefined) {
      params.directory_version = directoryVersion;
    }

    const res = await http.get<AgentDirectoryDetails>(
      `${HYPATIA_URL}/api/v1/directory/agents/${agentId}`,
      { params }
    );
    return res.data;
  };
}

getAgentDirectoryDetails.key = (agentId: string, directoryVersion?: number) =>
  ['opal:directory-agent-details', agentId, directoryVersion] as const;

export type {
  AgentDirectoryDetails,
  DirectoryAgent,
  DirectoryBrowseResponse,
  InstallAgentRequest,
  InstallAgentResponse,
  PublishAgentRequest,
  PublishAgentResponse,
};
